import classNames from 'clsx';
import { Card, Popover } from 'aui';
import isEmpty from 'lodash-es/isEmpty';
import React, { useCallback, useMemo } from 'react';
import { formatMessageWithLocale } from '../../../../locales';
import CopyableText from '../../../CopyableTextKit';
import { JsonSchemaViewer } from '../../../JsonSchemaViewer/JsonSchemaViewer';
import { MarkdownOpenExternalLinkCtxCustomer } from '../../ctx/OpenExternalLinkCtx';
import MarkdownViewer from '../../viewer';
import type { MdComponentIndexProps } from '../type';
import styles from './DataSchemaViewer.module.less';
import { useSchema } from './useSchema';
import { MarkdownSafeMdxViewerLocaleContext } from '../../../../components/MarkdownSafeMdxViewer/MarkdownSafeMdxViewerLocaleContext';
import { JsonSchemaViewerCopyableName } from '../../../../components/JsonSchemaViewer/components/PropertyNameRender';

type JSONSchema = any;

export function DataSchemaViewerFactory({
  locale,
  appLowerName,
}: Pick<React.ComponentProps<typeof MarkdownViewer>, 'locale' | 'appLowerName'>) {
  const DataSchemaViewer: React.FC<
    {
      title?: string;
      id: string;
      hideName?: boolean | string;
    } & MdComponentIndexProps
  > = function DataSchemaViewer({ hideName, title, id: schemaId, ...props }) {
    const localeContext = React.useContext(MarkdownSafeMdxViewerLocaleContext);
    const newLocale = locale || localeContext;
    const { schema, title: schemaTitle } = useSchema(schemaId);
    const propertyNameRender = JsonSchemaViewerCopyableName;
    const keyValueRenderMap = useMemo(
      () => ({
        enum: (
          values: string[],
          defaultValueNode: React.ReactNode,
          enumDescriptions?: JSONSchema['x-app-enum'],
        ) => {
          if (
            !enumDescriptions ||
            isEmpty(enumDescriptions) ||
            enumDescriptions.every((res) => res.value === '')
          ) {
            return defaultValueNode;
          }

          return (
            <Popover
              placement="top"
              title={null}
              content={() => {
                const enums =
                  enumDescriptions?.map((res) => {
                    return {
                      label: String(res.value !== undefined ? res.value : ''),
                      name: res.name,
                      value: res.description !== undefined ? res.description : '',
                    };
                  }) ?? [];
                return (
                  <div className={classNames('flex', styles['enum-popover'])}>
                    <div>
                      <div className={styles['enum-popover__title']}>
                        {formatMessageWithLocale(newLocale, { id: 'enumeratedValue' })}
                      </div>
                      {enums.map((res) => {
                        return (
                          <div key={res.label} className={styles['enum-popover__content']}>
                            <CopyableText text={res.label} copyText={res.label} />
                          </div>
                        );
                      })}
                    </div>
                    {enums.some((val) => val.name !== '') && (
                      <div>
                        <div className={styles['enum-popover__title']}>
                          {formatMessageWithLocale(newLocale, { id: 'name' })}
                        </div>
                        {enums.map((res) => {
                          return (
                            <div key={res.label} className={styles['enum-popover__content']}>
                              {res.name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div>
                      <div className={styles['enum-popover__title']}>
                        {formatMessageWithLocale(newLocale, { id: 'description' })}
                      </div>
                      {enums.map((res) => {
                        return (
                          <div key={res.label} className={styles['enum-popover__content']}>
                            {res.value}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            >
              <div className="cursor-pointer">{defaultValueNode}</div>
            </Popover>
          );
        },
      }),
      [],
    );

    // 正因为使用了自定义的MarkdownViewer 可以移除内置的 @stoplight/markdown-viewer
    const descriptionRender = useCallback((value: string) => {
      // 注意这个MarkdownViewer 不支持自定义组件
      return (
        <MarkdownOpenExternalLinkCtxCustomer
          fn={(data) => {
            return {
              openExternalLink: data.openExternalLink,
              imageReplaceHostOptions: data.imageReplaceHostOptions,
              imageSrcAddParams: data.imageSrcAddParams,
              renderExternalLink: data.renderExternalLink,
            };
          }}
        >
          {({
            openExternalLink,
            imageReplaceHostOptions,
            imageSrcAddParams,
            renderExternalLink,
          }) => {
            return (
              <MarkdownViewer
                value={value}
                renderExternalLink={renderExternalLink}
                openExternalLink={openExternalLink}
                imageReplaceHostOptions={imageReplaceHostOptions}
                imageSrcAddParams={imageSrcAddParams}
                locale={locale}
                enableDataSchema={false}
                enableHeadingSlugId={false}
                enableHeadingAutoLink={false}
                showErrorMsg={false}
                isSupportHash={false}
                appLowerName={appLowerName}
              />
            );
          }}
        </MarkdownOpenExternalLinkCtxCustomer>
      );
    }, []);

    return (
      <div
        data-markdown-component="x-dataschema-container"
        data-first={props['data-first']}
        data-last={props['data-last']}
      >
        <Card
          size="small"
          title={
            hideName === 'false' || hideName === false || hideName === undefined
              ? schemaTitle ?? title
              : null
          }
          className={classNames(styles.parameters__card)}
        >
          <div className="pl-4">
            <JsonSchemaViewer
              schema={schema}
              locale={locale}
              disableCrumbs
              className={styles['json-schema-viewer']}
              propertyNameRender={propertyNameRender}
              keyValueRenderMap={keyValueRenderMap}
              viewMode="standalone"
              descriptionRender={descriptionRender}
              isStateRightAligned={true}
              enableDescriptionShowInTitle
              isMergeAllOf
              appNameLower={appLowerName}
            />
          </div>
        </Card>
      </div>
    );
  };

  (
    DataSchemaViewer as React.FC<{
      title?: string;
      id: string;
    }> & {
      attributes: string[];
    }
  ).attributes = ['title', 'id', 'hideName'];
  return DataSchemaViewer as React.FC<{
    title?: string;
    id: string;
  }> & {
    attributes: string[];
  };
}
